import React, { FC, useState } from 'react'
import styled from 'styled-components'
import Octicon, { Package, Reply, Dependent } from '@primer/octicons-react'
import { atom, useAtom } from 'jotai'
import { Button, FormikInput } from '@viadelivery/design-system'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import Loader from 'react-loader-spinner'
import { useQueryClient } from 'react-query'
import { useToasts } from 'react-toast-notifications'

import { QUERY_TYPES, STATUS_LIST } from '~/constants'
import { updatePPNOrder } from '~/utils/helpers'
import { postData } from '~/utils/api'
import { Modal } from '~/components'
import { ConfirmModal } from "../index";

const ModalAcceptOrderAtom = atom(false)
const ModalIssueOrderAtom = atom(false)
const ModalReturnOrderAtom = atom(false)
const ModalConfirmAgeAtom = atom(false)
const AgeConfirmAtom = atom(-1)
const OrderAtom = atom(null)
const LoadingAtom = atom(false)

const onUpdateOrderSuccess = ({
  order,
  actionCompleted,
  queryClient,
  addToast,
  t,
}) => {
  const { ORDERS, ORDER_INFO } = QUERY_TYPES
  const id = order.id
  addToast(t(`Orders:action_status_${actionCompleted}`), {
    appearance: 'success',
  })
  queryClient.invalidateQueries(ORDERS)
  queryClient.removeQueries(ORDERS)
  queryClient.invalidateQueries([ORDER_INFO, { id }])
}

const onUpdateOrderError = ({ error, queryClient, addToast, t }) => {
  addToast(t(`Orders:${error}`), {
    appearance: 'error',
  })
}

const StyledGroup = styled.div`
  button + button {
    margin-left: 12px;
  }
`

export const ActionsButtonsGroup: FC<any> = ({ children }) => {
  return <StyledGroup>{children}</StyledGroup>
}

const ModalLoader = ({ t }) => {
  return (
    <div className="flex flex-col justify-center items-center py-5 pb-10">
      <Loader type="Oval" color="#F64F2A" height={50} width={50} />
      <span className="mt-2 text-gray-700">{t('Orders:Please wait')}</span>
    </div>
  )
}

export const AcceptOrderButton: FC<any> = ({ order, t, withLabel = false }) => {
  const { APPROVED, FIRST_MILE, IN_PROGRESS } = STATUS_LIST
  const [, setModalOpen] = useAtom(ModalAcceptOrderAtom)
  const [, setOrder] = useAtom(OrderAtom)

  return (
    <>
      {[APPROVED, FIRST_MILE, IN_PROGRESS].includes(order.status) && (
        <Button
          variant="info"
          onClick={() => {
            setModalOpen(true)
            setOrder(order)
          }}
          title={t('Orders:Accept order')}
        >
          <Octicon icon={Dependent} />
          {withLabel && <span className="ml-2">{t('Orders:Accept order')}</span>}
        </Button>
      )}
    </>
  )
}

export const IssueOrderButton: FC<any> = ({ order, t, withLabel = false }) => {
  const { PLACED_IN_POSTAMAT, EXPIRING } = STATUS_LIST
  const [, setModalOpen] = useAtom(ModalIssueOrderAtom)
  const [, setOrder] = useAtom(OrderAtom)
  const [, setAgeConfirm] = useAtom(AgeConfirmAtom)
  const [, setModalConfirmOpen] = useAtom(ModalConfirmAgeAtom)

  return (
    <>
      {[PLACED_IN_POSTAMAT, EXPIRING].includes(order?.status) && (
        <Button
          variant="info"
          onClick={async () => {
            const { customer_age_limit } = await postData(
              '/api/v1/get-dealer-settings',
              {
                dealer_id: order?.dealer_id,
              },
            )

            if (customer_age_limit > 0) {
              setModalConfirmOpen(true)
              setAgeConfirm(customer_age_limit)
            } else {
              setModalOpen(true)
            }

            setOrder(order)
          }}
          title={t('Orders:Issue order')}
        >
          <Octicon icon={Package} />
          {withLabel && <span className="ml-2">{t('Orders:Issue order')}</span>}
        </Button>
      )}
    </>
  )
}

export const ReturnOrderButton: FC<any> = ({ order, t, withLabel = false }) => {
  const { PLACED_IN_POSTAMAT, EXPIRING } = STATUS_LIST
  const [, setModalOpen] = useAtom(ModalReturnOrderAtom)
  const [, setOrder] = useAtom(OrderAtom)

  return (
    <>
      {[PLACED_IN_POSTAMAT, EXPIRING].includes(order.status) && (
        <Button
          variant="info"
          onClick={() => {
            setModalOpen(true)
            setOrder(order)
          }}
          title={t('Orders:Return a parcel label')}
        >
          <Octicon icon={Reply} />
          {withLabel && <span className="ml-2">{t('Orders:Return a parcel label')}</span>}
        </Button>
      )}
    </>
  )
}

export const ActionPPNOrdersModals: FC<any> = ({ t }) => {
  const { PICKED_UP, UNCLAIMED, PLACED_IN_POSTAMAT } = STATUS_LIST
  const [isModalIssueOrderOpen, setModalIssueOrderOpen] = useAtom(
    ModalIssueOrderAtom,
  )
  const [isModalReturnOrderOpen, setModalReturnOrderOpen] = useAtom(
    ModalReturnOrderAtom,
  )
  const [isModalAcceptOrderOpen, setModalAcceptOrderOpen] = useAtom(
    ModalAcceptOrderAtom,
  )
  const [isModalConfirmAgeOpen, setModalConfirmAgeOpen] = useAtom(
    ModalConfirmAgeAtom,
  )
  const [ageConfirm] = useAtom(AgeConfirmAtom)
  const [order]: any = useAtom(OrderAtom)
  const [loading, setLoading] = useAtom(LoadingAtom)
  const queryClient = useQueryClient()
  const { addToast } = useToasts()

  const pinCodeValidationSchema = Yup.object().shape({
    pin_code: Yup.string()
      .matches(/^[0-9]{6}$/, t('Should consist of 6 digits'))
      .required(t('Required')),
  })

  return (
    <>
      <Modal
        title={t('Orders:Accept order')}
        isOpen={isModalAcceptOrderOpen}
        setIsOpen={setModalAcceptOrderOpen}
        dialogClass={'ppn-order-modal'}
      >
        <h3 className="text-2xl font-bold mb-4">{t('Orders:Accept order')}</h3>
        {loading ? (
          <ModalLoader t={t} />
        ) : (
          <div>
            <p className="pb-6">
              {t('Orders:Accept an order', { ext_key: order?.ext_key })}
            </p>
            <div className="flex justify-between">
              <Button
                variant="info"
                onClick={() => setModalAcceptOrderOpen(false)}
              >
                {t('Back')}
              </Button>
              <Button
                onClick={() => {
                  setLoading(true)
                  updatePPNOrder({
                    action: PLACED_IN_POSTAMAT,
                    uuid: order?.key || order?.uuid,
                    onSuccess: actionCompleted => {
                      setModalAcceptOrderOpen(false)
                      setLoading(false)
                      onUpdateOrderSuccess({
                        order,
                        actionCompleted,
                        queryClient,
                        addToast,
                        t,
                      })
                    },
                    onError: error => {
                      setModalAcceptOrderOpen(false)
                      setLoading(false)
                      onUpdateOrderError({
                        error,
                        queryClient,
                        addToast,
                        t,
                      })
                    },
                  })
                }}
              >
                {t('Confirm')}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        title={t('Orders:Issue order')}
        isOpen={isModalIssueOrderOpen}
        setIsOpen={setModalIssueOrderOpen}
        dialogClass={'ppn-order-modal'}
      >
        <h3 className="text-2xl font-bold mb-4">{t('Orders:Issue order')}</h3>
        {loading ? (
          <ModalLoader t={t} />
        ) : (
          <Formik
            initialValues={{ pin_code: '' }}
            validationSchema={pinCodeValidationSchema}
            onSubmit={async values => {
              setLoading(true)

              updatePPNOrder({
                uuid: order?.key || order?.uuid,
                action: PICKED_UP,
                pin_code: values.pin_code,
                onSuccess: actionCompleted => {
                  setLoading(false)
                  setModalIssueOrderOpen(false)
                  onUpdateOrderSuccess({
                    order,
                    actionCompleted,
                    queryClient,
                    addToast,
                    t,
                  })
                },
                onError: error => {
                  setLoading(false)
                  setModalIssueOrderOpen(false)
                  onUpdateOrderError({ error, queryClient, addToast, t })
                },
              })
            }}
          >
            {() => (
              <Form>
                <p className="pb-6">
                  {t('Orders:Issue an order', { ext_key: order?.ext_key })}
                </p>
                <FormikInput
                  name="pin_code"
                  placeholder={t('Orders:Customer pin code')}
                />
                <div className="flex justify-end">
                  <input
                    type="submit"
                    value={t('Confirm')}
                    className="mt-4 px-3 py-2 rounded border border-brand-500 bg-brand-500 hover:bg-brand-600 hover:border-brand-600 text-white font-bold"
                  />
                </div>
              </Form>
            )}
          </Formik>
        )}
      </Modal>

      <Modal
        title={t('Orders:Return a parcel')}
        isOpen={isModalReturnOrderOpen}
        setIsOpen={setModalReturnOrderOpen}
        dialogClass={'ppn-order-modal'}
      >
        <h3 className="text-2xl font-bold mb-4">
          {t('Orders:Return a parcel')}
        </h3>
        {loading ? (
          <ModalLoader t={t} />
        ) : (
          <div>
            <p className="pb-6">
              {t('Orders:Return an order', { ext_key: order?.ext_key })}
            </p>
            <div className="flex justify-between">
              <Button
                variant="info"
                onClick={() => setModalReturnOrderOpen(false)}
              >
                {t('Back')}
              </Button>
              <Button
                onClick={() => {
                  setLoading(true)
                  updatePPNOrder({
                    action: UNCLAIMED,
                    uuid: order?.key || order?.uuid,
                    onSuccess: actionCompleted => {
                      setModalReturnOrderOpen(false)
                      setLoading(false)
                      onUpdateOrderSuccess({
                        order,
                        actionCompleted,
                        queryClient,
                        addToast,
                        t,
                      })
                    },
                    onError: error => {
                      setModalReturnOrderOpen(false)
                      setLoading(false)
                      onUpdateOrderError({
                        error,
                        queryClient,
                        addToast,
                        t,
                      })
                    },
                  })
                }}
              >
                {t('Confirm')}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {isModalConfirmAgeOpen && ageConfirm > 0 && (
        <ConfirmModal
          title={t('common:Warning')}
          onConfirm={() => {
            setModalConfirmAgeOpen(false)
            setModalIssueOrderOpen(true)
          }}
          t={t}
        >
          <p>
            {t(`common:Age confirm`, {
              minAge: ageConfirm,
            })}
          </p>
        </ConfirmModal>
      )}
    </>
  )
}
